/**
 * Video Job Card Component - Glass Morphism Design
 * Displays a video generation job with status and result
 * Validates: Requirements 17.4, 17.5, 17.6
 */
import { VideoJob } from '../../stores/videoStore';
import { Icons } from '../icons';
import { THEME } from '../../styles/theme';

interface VideoJobCardProps {
  job: VideoJob;
  isSelected?: boolean;
  onClick?: () => void;
}

export function VideoJobCard({ job, isSelected, onClick }: VideoJobCardProps) {
  const getStatusColor = (): string => {
    switch (job.status) {
      case 'completed':
        return THEME.success;
      case 'processing':
        return THEME.info;
      case 'failed':
        return THEME.error;
      default:
        return THEME.warning;
    }
  };

  const getStatusText = (): string => {
    switch (job.status) {
      case 'completed':
        return 'Completed';
      case 'processing':
        return 'Processing...';
      case 'failed':
        return 'Failed';
      default:
        return 'Pending';
    }
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  const truncatePrompt = (text: string, maxLength: number = 100) => {
    if (text.length <= maxLength) return text;
    return text.slice(0, maxLength) + '...';
  };

  return (
    <div
      onClick={onClick}
      className="rounded-xl overflow-hidden transition-all duration-200 border cursor-pointer"
      style={{
        backgroundColor: isSelected ? THEME.accentSoft : 'rgba(255,255,255,0.03)',
        borderColor: isSelected ? THEME.accentBorder : THEME.border,
      }}
      onMouseEnter={(e) => {
        if (!isSelected) {
          e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.05)';
        }
      }}
      onMouseLeave={(e) => {
        if (!isSelected) {
          e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.03)';
        }
      }}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-3 p-4 pb-3">
        <div className="flex items-center gap-2">
          <span
            className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
              job.status === 'processing' ? 'animate-pulse' : ''
            }`}
            style={{
              backgroundColor: 'rgba(0,0,0,0.3)',
              color: getStatusColor(),
            }}
          >
            {getStatusText()}
          </span>
          {job.progress !== undefined && job.status === 'processing' && (
            <span className="text-xs" style={{ color: THEME.textMuted }}>
              {job.progress}%
            </span>
          )}
        </div>
        <span className="text-xs" style={{ color: THEME.textMuted }}>
          {formatDate(job.createdAt)}
        </span>
      </div>

      {/* Thumbnail or Placeholder */}
      {job.video?.thumbnailUrl ? (
        <div
          className="relative aspect-video"
          style={{ backgroundColor: THEME.bgSurface }}
        >
          <img
            src={job.video.thumbnailUrl}
            alt="Video thumbnail"
            className="w-full h-full object-cover"
          />
          {job.video.duration && (
            <span
              className="absolute bottom-2 right-2 text-xs px-1.5 py-0.5 rounded font-medium"
              style={{
                backgroundColor: 'rgba(0,0,0,0.7)',
                backdropFilter: 'blur(10px)',
                color: THEME.textMain,
              }}
            >
              {job.video.duration}s
            </span>
          )}
        </div>
      ) : (
        <div
          className="aspect-video flex items-center justify-center"
          style={{ backgroundColor: THEME.bgSurface }}
        >
          {job.status === 'processing' ? (
            <div className="text-center">
              <Icons.Refresh
                size={32}
                className="animate-spin mx-auto mb-2"
                style={{ color: THEME.accentColor }}
              />
              <p className="text-xs" style={{ color: THEME.textMuted }}>
                Generating...
              </p>
            </div>
          ) : job.status === 'failed' ? (
            <div className="text-center">
              <Icons.X
                size={32}
                className="mx-auto mb-2"
                style={{ color: THEME.error }}
              />
              <p className="text-xs" style={{ color: THEME.error }}>
                Failed
              </p>
            </div>
          ) : (
            <Icons.Video size={32} style={{ color: THEME.textMuted }} />
          )}
        </div>
      )}

      {/* Info */}
      <div className="p-4 pt-3">
        <p className="text-sm mb-2 line-clamp-2" style={{ color: THEME.textMain }}>
          {truncatePrompt(job.enhancedPrompt || job.prompt)}
        </p>

        <div className="flex items-center gap-2 text-xs" style={{ color: THEME.textMuted }}>
          <span>{job.model}</span>
          {job.settings.duration && (
            <>
              <span>•</span>
              <span>{job.settings.duration}s</span>
            </>
          )}
          {job.settings.resolution && (
            <>
              <span>•</span>
              <span>{job.settings.resolution}</span>
            </>
          )}
        </div>

        {/* Error Message */}
        {job.error && (
          <p
            className="mt-2 text-xs p-2 rounded-lg"
            style={{
              color: THEME.error,
              backgroundColor: THEME.errorSoft,
            }}
          >
            {job.error}
          </p>
        )}
      </div>
    </div>
  );
}
